/* Fibblewick — personalization form
 *
 * Flow: describe child -> photos -> format -> add to cart
 *
 * Two modes:
 *   demo  — no backend configured. Photos stay in the browser. The whole flow is
 *           clickable so you can test and demo before the server exists.
 *   live  — uploads go straight to private storage via a short-lived signed URL,
 *           then the character record is created, then the item is added to cart.
 *
 * Nothing secret lives in this file. All writes go through the app proxy.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'fibblewick:draft:v1';
  var MAX_EDGE = 4000;      // downscale beyond this; still far above print need
  var JPEG_QUALITY = 0.9;   // compress quality, never dimensions, or prints go soft
  var MAX_BYTES = 25 * 1024 * 1024;

  function init(root) {
    var cfg = JSON.parse(root.getAttribute('data-config') || '{}');
    var canUpload = Boolean(cfg.uploadUrl);        // phase 1 — needed to sell
    var canSaveCharacter = Boolean(cfg.characterUrl); // phase 2 — saved profiles

    var el = {
      form:     root.querySelector('[data-fw-form]'),
      progress: root.querySelectorAll('[data-fw-progress] li'),
      steps:    root.querySelectorAll('[data-fw-form] .fw__step'),
      about:    root.querySelector('[data-fw-about]'),
      count:    root.querySelector('[data-fw-count]'),
      prompts:  root.querySelector('[data-fw-prompts]'),
      drop:     root.querySelector('[data-fw-drop]'),
      file:     root.querySelector('[data-fw-file]'),
      thumbs:   root.querySelector('[data-fw-thumbs]'),
      variants: root.querySelector('[data-fw-variants]'),
      consent:  root.querySelector('[data-fw-consent]'),
      language: root.querySelector('[data-fw-language]'),
      back:     root.querySelector('[data-fw-back]'),
      next:     root.querySelector('[data-fw-next]'),
      submit:   root.querySelector('[data-fw-submit]'),
      status:   root.querySelector('[data-fw-status]')
    };

    var state = {
      step: 1,
      about: '',
      photos: [],          // { id, file, url, w, h }
      variantId: null,
      consent: false,      // never restored from a draft — must be given each time
      language: '',
      busy: false
    };

    /* ---------------- persistence ----------------
       Text and the chosen format survive a refresh. Files cannot be serialised,
       so photos are re-picked. That is the honest trade-off; warn rather than
       pretend they are still there. */

    function save() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          about: state.about,
          variantId: state.variantId,
          language: state.language
        }));
      } catch (e) { /* private mode, quota — not worth failing over */ }
    }

    function restore() {
      try {
        var raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return;
        var saved = JSON.parse(raw);
        if (saved.about) { state.about = saved.about; el.about.value = saved.about; }
        if (saved.variantId) state.variantId = saved.variantId;
        // Consent is intentionally not restored: it must be given deliberately
        // each time, not inherited from an abandoned draft.
        if (saved.language && el.language) { state.language = saved.language; el.language.value = saved.language; }
      } catch (e) { /* ignore corrupt drafts */ }
    }

    /* ---------------- rendering ---------------- */

    function render() {
      Array.prototype.forEach.call(el.steps, function (s) {
        s.hidden = Number(s.getAttribute('data-step')) !== state.step;
      });

      Array.prototype.forEach.call(el.progress, function (li) {
        var n = Number(li.getAttribute('data-step'));
        li.setAttribute('data-state', n === state.step ? 'active' : (n < state.step ? 'done' : 'todo'));
      });

      el.back.hidden = state.step === 1;
      el.next.hidden = state.step === 3;
      el.submit.hidden = state.step !== 3;

      el.count.textContent = String(state.about.trim().length);
      renderThumbs();
      renderVariants();
    }

    function renderThumbs() {
      el.thumbs.innerHTML = '';
      state.photos.forEach(function (p) {
        var li = document.createElement('li');
        var img = document.createElement('img');
        img.src = p.url;
        img.alt = '';
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.innerHTML = '&times;';
        btn.setAttribute('aria-label', 'Remove photo');
        btn.addEventListener('click', function () { removePhoto(p.id); });
        li.appendChild(img);
        li.appendChild(btn);
        el.thumbs.appendChild(li);
      });

      var room = state.photos.length < cfg.maxPhotos;
      el.drop.hidden = !room;
    }

    function renderVariants() {
      if (el.variants.childElementCount) return updateVariantState();

      (cfg.variants || []).forEach(function (v) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'fw__variant';
        b.setAttribute('role', 'radio');
        b.setAttribute('data-variant-id', v.id);
        if (!v.available) b.disabled = true;

        var note = !v.available ? 'Sold out'
                 : (v.requiresShipping ? 'Printed and posted to you' : 'Instant download');

        b.innerHTML =
          '<span><span class="fw__variant-title"></span>' +
          '<span class="fw__variant-note"></span></span>' +
          '<span class="fw__variant-price"></span>';
        b.querySelector('.fw__variant-title').textContent = v.title;
        b.querySelector('.fw__variant-note').textContent = note;
        b.querySelector('.fw__variant-price').textContent = v.priceFormatted;

        b.addEventListener('click', function () {
          state.variantId = v.id;
          save();
          updateVariantState();
          clearError(3);
        });
        el.variants.appendChild(b);
      });
      updateVariantState();
    }

    function updateVariantState() {
      Array.prototype.forEach.call(el.variants.children, function (b) {
        b.setAttribute('aria-checked', String(Number(b.getAttribute('data-variant-id')) === state.variantId));
      });
    }

    /* ---------------- errors ---------------- */

    function showError(step, msg) {
      var p = root.querySelector('[data-fw-error="' + step + '"]');
      if (!p) return;
      p.textContent = msg;
      p.hidden = false;
    }
    function clearError(step) {
      var p = root.querySelector('[data-fw-error="' + step + '"]');
      if (p) { p.hidden = true; p.textContent = ''; }
    }
    function setStatus(msg) { el.status.textContent = msg || ''; }

    /* ---------------- photos ---------------- */

    function readDimensions(file) {
      return new Promise(function (resolve, reject) {
        var url = URL.createObjectURL(file);
        var img = new Image();
        img.onload = function () {
          resolve({ w: img.naturalWidth, h: img.naturalHeight, url: url });
        };
        img.onerror = function () {
          // HEIC and some formats will not decode in the browser. Let them
          // through unmeasured rather than blocking an iPhone customer.
          URL.revokeObjectURL(url);
          resolve(null);
        };
        img.src = url;
      });
    }

    function compress(file, dims) {
      // Re-encode at lower quality but keep the pixel dimensions, unless the
      // image is enormous. Shrinking dimensions is what makes printed books blurry.
      if (!dims || file.type === 'image/heic') return Promise.resolve(file);

      var scale = Math.min(1, MAX_EDGE / Math.max(dims.w, dims.h));
      if (scale === 1 && file.size < 4 * 1024 * 1024) return Promise.resolve(file);

      return new Promise(function (resolve) {
        var img = new Image();
        img.onload = function () {
          var c = document.createElement('canvas');
          c.width = Math.round(dims.w * scale);
          c.height = Math.round(dims.h * scale);
          c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
          c.toBlob(function (blob) {
            // Never accept a "compressed" file that came out bigger.
            if (!blob || blob.size >= file.size) return resolve(file);
            resolve(new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' }));
          }, 'image/jpeg', JPEG_QUALITY);
        };
        img.onerror = function () { resolve(file); };
        img.src = dims.url;
      });
    }

    async function addFiles(fileList) {
      clearError(2);
      var files = Array.prototype.slice.call(fileList);
      var room = cfg.maxPhotos - state.photos.length;

      if (files.length > room) {
        showError(2, 'You can add up to ' + cfg.maxPhotos + ' photos, so only the first ' + room + ' were added.');
        files = files.slice(0, room);
      }

      for (var i = 0; i < files.length; i++) {
        var file = files[i];

        if (!/^image\//.test(file.type)) {
          showError(2, '"' + file.name + '" is not an image.');
          continue;
        }
        if (file.size > MAX_BYTES) {
          showError(2, '"' + file.name + '" is too large. Maximum 25MB.');
          continue;
        }

        var dims = await readDimensions(file);

        if (dims && Math.min(dims.w, dims.h) < cfg.minPhotoWidth) {
          URL.revokeObjectURL(dims.url);
          showError(2,
            '"' + file.name + '" is too small to print well (' + dims.w + '×' + dims.h +
            '). Please use a photo at least ' + cfg.minPhotoWidth + ' pixels on the shortest side — ' +
            'the original from your camera roll usually works.');
          continue;
        }

        var prepared = await compress(file, dims);
        state.photos.push({
          id: 'p' + Date.now() + '_' + i,
          file: prepared,
          url: dims ? dims.url : URL.createObjectURL(file),
          w: dims ? dims.w : null,
          h: dims ? dims.h : null
        });
      }

      renderThumbs();
    }

    function removePhoto(id) {
      state.photos = state.photos.filter(function (p) {
        if (p.id === id) { URL.revokeObjectURL(p.url); return false; }
        return true;
      });
      clearError(2);
      renderThumbs();
    }

    /* ---------------- validation ---------------- */

    function validate(step) {
      if (step === 1) {
        if (state.about.trim().length < cfg.minChars) {
          showError(1, 'Please write at least ' + cfg.minChars + ' characters so we have something to work with.');
          return false;
        }
      }
      if (step === 2) {
        if (state.photos.length < cfg.minPhotos) {
          showError(2, 'Please add at least ' + cfg.minPhotos + ' photo.');
          return false;
        }
        if (el.consent && !state.consent) {
          showError(2, 'Please confirm you have permission to provide these photos.');
          return false;
        }
      }
      if (step === 3) {
        if (!state.variantId) {
          showError(3, 'Please choose a format.');
          return false;
        }
      }
      clearError(step);
      return true;
    }

    /* ---------------- submit ---------------- */

    // One batch id groups every photo from a single order, so the production team
    // opens one link and sees all of them together.
    function newBatchId() {
      if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
      return 'b' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
    }

    async function uploadPhoto(photo, batch, index) {
      var fd = new FormData();
      fd.append('file', photo.file);
      fd.append('batch', batch);
      fd.append('index', String(index));

      var res = await fetch(cfg.uploadUrl, { method: 'POST', body: fd });
      if (!res.ok) {
        var detail = await res.text().catch(function () { return ''; });
        throw new Error(detail || 'That photo would not upload.');
      }
      var out = await res.json();
      return out.key;
    }

    async function createCharacter(photoKeys) {
      var res = await fetch(cfg.characterUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ about: state.about.trim(), photoKeys: photoKeys })
      });
      if (!res.ok) throw new Error('Could not save the details.');
      return res.json(); // { id, token }
    }

    async function addToCart(result) {
      var summary = state.about.trim();
      if (summary.length > 120) summary = summary.slice(0, 117) + '…';

      // Underscore-prefixed properties are hidden from the customer in cart and
      // checkout, but still saved on the order and visible in the admin.
      var props = {
        'Personalisation': summary,
        '_about': state.about.trim(),
        '_photo_count': state.photos.length
      };
      if (state.language.trim()) props['Book language'] = state.language.trim();
      // Timestamped so there is a record of when consent was given, not just that
      // the box was ticked at some point.
      if (state.consent) props['_guardian_consent'] = 'Confirmed ' + new Date().toISOString();
      if (result.batch) props['_photo_batch'] = result.batch;
      if (result.characterId) props['_character_id'] = result.characterId;
      if (result.characterToken) props['_character_token'] = result.characterToken;

      var res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: state.variantId, quantity: 1, properties: props }]
        })
      });
      if (!res.ok) {
        var err = await res.json().catch(function () { return {}; });
        throw new Error(err.description || 'Could not add to cart.');
      }
    }

    async function submit() {
      if (state.busy) return;
      if (!validate(1)) { state.step = 1; render(); return; }
      if (!validate(2)) { state.step = 2; render(); return; }
      if (!validate(3)) return;

      state.busy = true;
      el.submit.disabled = true;
      clearError(3);

      try {
        var result = {};

        if (canUpload) {
          result.batch = newBatchId();
          var keys = [];
          for (var i = 0; i < state.photos.length; i++) {
            setStatus('Uploading photo ' + (i + 1) + ' of ' + state.photos.length + '…');
            keys.push(await uploadPhoto(state.photos[i], result.batch, i));
          }

          // Saved characters are phase 2. Until the endpoint exists, the order
          // still carries everything the team needs to make the book.
          if (canSaveCharacter) {
            setStatus('Saving your details…');
            var character = await createCharacter(keys);
            result.characterId = character.id;
            result.characterToken = character.token;
          }
        } else {
          setStatus('Demo mode — photos are not uploaded.');
          result.batch = 'demo-' + newBatchId().slice(0, 8);
        }

        setStatus('Adding to your cart…');
        await addToCart(result);

        try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}

        // Blank redirect means "stay here" — used by the local preview page, and
        // useful later if you want an on-page cart drawer instead of a page load.
        if (cfg.redirectTo) {
          window.location.href = cfg.redirectTo;
        } else {
          setStatus('');
          state.busy = false;
          root.dispatchEvent(new CustomEvent('fibblewick:added', {
            bubbles: true,
            detail: { character: character, variantId: state.variantId, photos: state.photos.length }
          }));
        }

      } catch (err) {
        showError(3, err.message + ' Please try again — nothing has been charged.');
        setStatus('');
        state.busy = false;
        el.submit.disabled = false;
      }
    }

    /* ---------------- events ---------------- */

    el.about.addEventListener('input', function () {
      state.about = el.about.value;
      el.count.textContent = String(state.about.trim().length);
      if (state.about.trim().length >= cfg.minChars) clearError(1);
      save();
    });

    el.prompts.addEventListener('click', function (e) {
      var btn = e.target.closest('button[data-hint]');
      if (!btn) return;
      var hint = btn.getAttribute('data-hint');
      var current = el.about.value;
      var prefix = current && !/\n$/.test(current) ? '\n' : '';
      el.about.value = current + prefix + hint;
      el.about.focus();
      el.about.setSelectionRange(el.about.value.length, el.about.value.length);
      el.about.dispatchEvent(new Event('input'));
    });

    if (el.consent) {
      el.consent.addEventListener('change', function () {
        state.consent = el.consent.checked;
        if (state.consent) clearError(2);
      });
      // The label contains a link to the Privacy Policy; clicking it should open
      // the policy, not toggle the checkbox.
      var consentLabel = root.querySelector('[data-fw-consent-label]');
      if (consentLabel) {
        consentLabel.addEventListener('click', function (e) {
          if (e.target.closest('a')) e.stopPropagation();
        });
      }
    }

    if (el.language) {
      el.language.addEventListener('input', function () {
        state.language = el.language.value;
        save();
      });
    }

    el.drop.addEventListener('click', function () { el.file.click(); });
    el.drop.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); el.file.click(); }
    });
    el.file.addEventListener('change', function () {
      addFiles(el.file.files);
      el.file.value = ''; // let the same file be re-picked after removal
    });

    ['dragenter', 'dragover'].forEach(function (t) {
      el.drop.addEventListener(t, function (e) { e.preventDefault(); el.drop.setAttribute('data-over', 'true'); });
    });
    ['dragleave', 'drop'].forEach(function (t) {
      el.drop.addEventListener(t, function (e) { e.preventDefault(); el.drop.removeAttribute('data-over'); });
    });
    el.drop.addEventListener('drop', function (e) {
      if (e.dataTransfer && e.dataTransfer.files) addFiles(e.dataTransfer.files);
    });

    el.next.addEventListener('click', function () {
      if (!validate(state.step)) return;
      state.step = Math.min(3, state.step + 1);
      render();
      root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    el.back.addEventListener('click', function () {
      state.step = Math.max(1, state.step - 1);
      render();
      root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    el.form.addEventListener('submit', function (e) { e.preventDefault(); submit(); });

    window.addEventListener('beforeunload', function (e) {
      if (state.photos.length && !state.busy) { e.preventDefault(); e.returnValue = ''; }
    });

    restore();
    render();
    if (!canUpload) console.info('[Fibblewick] Demo mode — set the upload endpoint to go live.');
  }

  function boot() {
    document.querySelectorAll('[data-fw-personalize]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init when the section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-fw-personalize]');
    if (root) init(root);
  });
})();
